<template>
  <div
    v-if="uiStore.infoDrawerOpen"
    class="drawer-backdrop"
    @click.self="closeDrawer"
  ></div>
  <div
    class="side-info-panel"
    :class="{
      'materialize-anim': shouldAnimate,
      'is-open': uiStore.infoDrawerOpen,
    }"
    @animationend="handleAnimationEnd"
  >
    <!-- CSS Dust Particles -->
    <div v-if="shouldAnimate" class="dust-particles">
      <div class="particle" v-for="n in 20" :key="n" :style="getParticleStyle(n)"></div>
    </div>

    <!-- Panel Content -->
    <div class="panel-content glass-panel">
      <header v-if="type === 'hero'" class="drawer-header mobile-drawer-header">
        <h2>Hero Stats</h2>
        <button
          type="button"
          class="drawer-close"
          aria-label="Close"
          title="Close"
          @click="closeDrawer"
        >
          <i class="fas fa-times"></i>
        </button>
      </header>
      <!-- Tabs Header -->
      <div class="panel-tabs" v-if="type === 'quest'">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'units' }"
          @click="activeTab = 'units'"
        >
          Units
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'assigned' }"
          @click="activeTab = 'assigned'"
        >
          Assigned
        </button>
        <button
          type="button"
          class="drawer-close mobile-drawer-close"
          aria-label="Close"
          title="Close"
          @click="closeDrawer"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="panel-tabs city-tabs" v-else-if="type === 'city'">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'stats' }"
          @click="activeTab = 'stats'"
        >
          City Stats
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'army' }"
          @click="activeTab = 'army'"
        >
          Defense Formation
        </button>
        <button
          type="button"
          class="drawer-close mobile-drawer-close"
          aria-label="Close"
          title="Close"
          @click="closeDrawer"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <!-- Tab Content -->
      <div class="tab-content scrollable">
        <div v-if="type === 'quest' && activeTab === 'units'" class="units-tab">
          <h2 class="panel-title">Assign Units</h2>
          <p class="drag-hint desktop-only">Drag a hero onto a contract to assign them.</p>
          <p class="drag-hint mobile-only">Tap a contract's hero slot to assign them.</p>
          <div
            v-for="hero in gameStore.playerHeroes"
            :key="hero.id"
            class="unit-card"
            :class="{
              busy: isHeroInProgress(hero.id),
              queued: plannedQuestTitle(hero.id) && !isHeroInProgress(hero.id),
            }"
            :draggable="!isHeroInProgress(hero.id)"
            @dragstart="onHeroDragStart($event, hero)"
          >
            <HeroTagChip
              block
              :name="heroDisplayName(hero)"
              :meta="`Hero · Level ${hero.level || 1}`"
              :image="socketedCard(hero)?.image || ''"
              :image-position="socketedCard(hero)?.image_position"
              :rarity="socketedCard(hero)?.rarity || 'common'"
            >
              <span v-if="isHeroInProgress(hero.id)" class="unit-status">On a mission</span>
              <span v-else-if="plannedQuestTitle(hero.id)" class="unit-status">
                Queued: {{ plannedQuestTitle(hero.id) }}
              </span>
            </HeroTagChip>
          </div>
          <p v-if="!gameStore.playerHeroes.length" class="placeholder-text">
            Activate a hero to assign them.
          </p>
          <QuestTroopAssign
            v-if="showTroopSliders"
            class="panel-troop-assign"
            :quest-slug="focusedQuestSlug"
            :subtitle="focusedQuestTitle"
          />
          <p v-else class="placeholder-text">{{ troopHint }}</p>
        </div>

        <div v-else-if="type === 'quest' && activeTab === 'assigned'" class="assigned-tab">
          <h2 class="panel-title">Expedition Roster</h2>
          <div v-if="gameStore.plannedRosters.length">
            <div
              v-for="roster in gameStore.plannedRosters"
              :key="roster.slug"
              class="stat-row roster-row"
            >
              <span class="stat-label">{{ roster.title }}</span>
              <span class="stat-value">
                {{ rosterHeroLabel(roster) }}
                <span class="troop-summary">{{ troopSummary(roster.assigned_troops) }}</span>
              </span>
            </div>
          </div>
          <p v-else class="placeholder-text">Drop a hero onto a contract or assign troops to build a roster.</p>
        </div>

        <div v-else-if="activeTab === 'stats'" class="stats-tab">
          <HeroStatsTab v-if="type === 'hero'" />
          <!-- City Stats -->
          <div v-else-if="type === 'city'" class="city-economy">
            <h2 class="panel-title">{{ data?.name || 'Your City' }}</h2>

            <section class="economy-section stock">
              <h3>City Stock</h3>
              <ul v-if="cityStock.length" class="economy-list">
                <li v-for="row in cityStock" :key="'stock-' + row.key">
                  <i :class="row.icon" :style="{ color: row.color }"></i>
                  <span class="economy-name">{{ row.name }}</span>
                  <span class="economy-amount">{{ row.amount }}</span>
                </li>
              </ul>
              <p v-else class="economy-empty">No city stock yet.</p>
            </section>

            <section class="economy-section yields">
              <h3>City Yields</h3>
              <ul v-if="cityYields.length" class="economy-list">
                <li v-for="row in cityYields" :key="'yield-' + row.key">
                  <i :class="row.icon" :style="{ color: row.color }"></i>
                  <span class="economy-name">{{ row.name }}</span>
                  <span class="economy-amount yield">+{{ row.amount }}/h</span>
                </li>
              </ul>
              <p v-else class="economy-empty">No yields this hour.</p>
            </section>

            <section class="economy-section upkeep">
              <h3>City Upkeep</h3>
              <ul v-if="cityUpkeep.length" class="economy-list">
                <li v-for="row in cityUpkeep" :key="'upkeep-' + row.key">
                  <i :class="row.icon" :style="{ color: row.color }"></i>
                  <span class="economy-name">{{ row.name }}</span>
                  <span class="economy-amount upkeep">-{{ row.amount }}/h</span>
                </li>
              </ul>
              <p v-else class="economy-empty">No upkeep this hour.</p>
            </section>
          </div>
        </div>

        <div v-else-if="type === 'city' && activeTab === 'army'" class="army-tab">
          <CityArmyPanel />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, defineProps } from 'vue';
import { useGameStore } from '../store/game';
import { useUiStore } from '../store/ui';
import HeroTagChip from './HeroTagChip.vue';
import HeroStatsTab from './HeroStatsTab.vue';
import CityArmyPanel from './CityArmyPanel.vue';
import QuestTroopAssign from './QuestTroopAssign.vue';
import {
  RESOURCE_DISPLAY_KEYS,
  resourceDisplay,
  projectResourceStocks,
} from '../utils/resourceDisplay';
import { questAllowsTroops, assignedTroopCount } from '../utils/questChance';
import { UNIT_SLUGS, unitDisplayMap } from '../utils/unitDisplay';

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  data: {
    type: Object,
    default: () => ({}),
  },
});

const gameStore = useGameStore();
const uiStore = useUiStore();
const activeTab = ref(props.type === 'quest' ? 'units' : 'stats');

function closeDrawer() {
  uiStore.setInfoDrawerOpen(false);
}

function onKeydown(event) {
  if (event.key === "Escape" && uiStore.infoDrawerOpen) {
    closeDrawer();
  }
}

watch(
  () => props.type,
  (type) => {
    activeTab.value = type === 'quest' ? 'units' : 'stats';
  }
);

watch(activeTab, (tab) => {
  if (props.type === 'city' && tab !== 'army' && uiStore.isEditingDefense) {
    gameStore.cancelDefenseEdit();
  }
});

const heroDisplayName = (hero) => {
  if (!hero) return 'Hero';
  return hero.name || `Hero #${hero.id}`;
};

const socketedCard = (hero) => gameStore.socketedHeroCard(hero?.id);

const isHeroInProgress = (heroId) => gameStore.inProgressHeroIds.has(heroId);

const plannedQuestTitle = (heroId) => {
  const roster = gameStore.plannedRosters.find((r) => r.hero?.id === heroId);
  return roster?.title || null;
};

const onHeroDragStart = (event, hero) => {
  if (!hero?.id || isHeroInProgress(hero.id) || window.innerWidth <= 768) {
    event.preventDefault();
    return;
  }
  const payload = JSON.stringify({ type: 'hero', id: hero.id });
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('text/plain', payload);
};

const focusedQuest = computed(() => gameStore.focusedQuest);
const focusedQuestSlug = computed(() => gameStore.focusedQuestSlug);
const focusedQuestTitle = computed(() => focusedQuest.value?.title || "Selected contract");
const showTroopSliders = computed(() => {
  if (!focusedQuestSlug.value || !focusedQuest.value) return false;
  if (focusedQuest.value.status === "in_progress") return false;
  return questAllowsTroops(focusedQuest.value);
});
const troopHint = computed(() => {
  if (!focusedQuestSlug.value || !focusedQuest.value) {
    return "Expand a contract to assign troops.";
  }
  if (focusedQuest.value.status === "in_progress") {
    return "Troops on an active mission are already in the field.";
  }
  return "This contract is hero and gold only.";
});

const rosterHeroLabel = (roster) => {
  if (roster?.hero) {
    return `${heroDisplayName(roster.hero)} (Lv ${roster.hero.level || 1})`;
  }
  return "Troops only";
};

const troopSummary = (troops) => {
  if (!assignedTroopCount(troops)) return "No troops";
  return UNIT_SLUGS.filter((unitSlug) => (Number(troops?.[unitSlug]) || 0) > 0)
    .map((unitSlug) => {
      const name = unitDisplayMap[unitSlug]?.name || unitSlug;
      return `${troops[unitSlug]} ${name}`;
    })
    .join(", ");
};

// Determine if we should animate on mount
const shouldAnimate = ref(false);

onMounted(() => {
  window.addEventListener("keydown", onKeydown);
  if (props.type === 'hero' && gameStore.justActivatedHero) {
    shouldAnimate.value = true;
  } else if (props.type === 'city' && gameStore.justFoundedCity) {
    shouldAnimate.value = true;
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown);
});

const handleAnimationEnd = () => {
  shouldAnimate.value = false; // Turn off animation class
  
  // Clear the flags in store if needed, though they won't harm if they stay
  // until next page reload, since the panel isn't re-mounted when switching tabs
  // wait, the panel WILL be re-mounted if we use v-if per route, so we SHOULD clear them.
  if (props.type === 'hero') gameStore.justActivatedHero = false;
  if (props.type === 'city') gameStore.justFoundedCity = false;
};

// Utilities for random dust particles
const MathRand = (min, max) => Math.random() * (max - min) + min;
const getParticleStyle = (n) => {
  return {
    left: `${MathRand(0, 100)}%`,
    top: `${MathRand(0, 100)}%`,
    animationDelay: `${MathRand(0, 0.5)}s`,
    animationDuration: `${MathRand(1, 2)}s`,
    transform: `scale(${MathRand(0.5, 1.5)})`
  };
};

const cityYields = computed(() => cityRateRows("produces"));
const cityUpkeep = computed(() => cityRateRows("consumes"));
const cityStock = computed(() => {
  const city = gameStore.activeCityEconomy;
  if (!city) return [];
  const projected = projectResourceStocks(
    city,
    city.rates,
    city.last_ticked_at || gameStore.globalAggregation?.last_ticked_at,
    gameStore.clockOffsetMs,
    gameStore.tickIntervalMs
  );
  const display = resourceDisplay("population");
  const pop = Math.floor(Number(projected.population) || 0);
  return [
    {
      key: "population",
      amount: pop,
      name: display.name,
      icon: display.icon,
      color: display.color,
    },
  ];
});

function cityRateRows(field) {
  const rates = gameStore.activeCityRates || {};
  return RESOURCE_DISPLAY_KEYS.filter(
    (key) => (Number(rates[key]?.[field]) || 0) > 0
  ).map((key) => {
    const display = resourceDisplay(key);
    return {
      key,
      amount: Number(rates[key][field]),
      name: display.name,
      icon: display.icon,
      color: display.color,
    };
  });
}
</script>

<style scoped>
.side-info-panel {
  position: fixed;
  right: 0;
  top: 70px;
  bottom: 80px;
  width: 350px;
  z-index: 800;
  display: flex;
  flex-direction: column;
}

/* Materialize effect base states */
.materialize-anim {
  animation: panel-materialize 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  opacity: 0;
  /* Starting transform could involve a slight blur and translate */
}

@keyframes panel-materialize {
  0% {
    opacity: 0;
    transform: translateX(50px) scale(0.95);
    filter: blur(10px) brightness(2);
  }
  50% {
    opacity: 0.8;
    filter: blur(2px) brightness(1.5);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
    filter: blur(0) brightness(1);
  }
}

.glass-panel {
  flex-grow: 1;
  background-color: rgba(20, 20, 30, 0.85); /* Dark base */
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-left: 1px solid rgba(0, 246, 255, 0.3);
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(0, 246, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
}

/* Dust particles */
.dust-particles {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  pointer-events: none;
  z-index: 900;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background-color: #00f6ff;
  border-radius: 50%;
  box-shadow: 0 0 8px #00f6ff, 0 0 12px #00f6ff;
  opacity: 0;
  animation: particle-fade-float 2s ease-out forwards;
}

@keyframes particle-fade-float {
  0% {
    opacity: 0;
    transform: translateY(20px) scale(1);
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-40px) scale(0);
  }
}

/* Tabs */
.panel-tabs {
  display: flex;
  border-bottom: 1px solid rgba(0, 246, 255, 0.2);
  background: rgba(36, 40, 59, 0.6);
}

.tab-btn {
  flex: 1;
  background: none;
  border: none;
  color: #a0a0b0;
  padding: 15px 0;
  font-family: 'Orbitron', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.tab-btn:hover {
  color: #00f6ff;
  background: rgba(0, 246, 255, 0.05);
}

.tab-btn.active {
  color: #00f6ff;
  background: rgba(0, 246, 255, 0.1);
}

.tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #00f6ff;
  box-shadow: 0 0 10px #00f6ff;
}

.city-tabs .tab-btn {
  font-size: 0.68rem;
  letter-spacing: 0.5px;
  padding: 12px 4px;
}

/* Tab Content */
.tab-content {
  flex-grow: 1;
  padding: 20px;
  overflow-y: auto;
  color: #e0e0e0;
}

.panel-title {
  font-family: 'Orbitron', sans-serif;
  color: #00f6ff;
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 20px;
  text-align: center;
  text-shadow: 0 0 10px rgba(0, 246, 255, 0.3);
}

/* Stats Styling */
.stat-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-label {
  color: #a0a0b0;
}
.stat-value {
  font-weight: 600;
  color: #fff;
}

/* City Yields / Upkeep */
.city-economy {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.economy-section h3 {
  margin: 0 0 0.6rem;
  font-family: "Cinzel", serif;
  font-size: 0.85rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #c9b48a;
}

.economy-section.upkeep h3 {
  color: #c48a8a;
}

.economy-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.economy-list li {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
}

.economy-list i {
  width: 22px;
  text-align: center;
  font-size: 1.05rem;
}

.economy-name {
  flex: 1;
  color: #d8d0c0;
  font-size: 0.9rem;
}

.economy-amount {
  font-weight: 700;
  font-size: 0.9rem;
}

.economy-amount.yield {
  color: #7dffa3;
}

.economy-amount.upkeep {
  color: #ff8a8a;
}

.economy-empty {
  margin: 0;
  color: #7a8490;
  font-size: 0.8rem;
  font-style: italic;
}

.placeholder-text {
  text-align: center;
  color: #888;
  margin-bottom: 20px;
  font-style: italic;
}

.unit-card {
  margin-bottom: 16px;
  cursor: grab;
  user-select: none;
  transition: box-shadow 0.2s ease;
}

.unit-card:hover {
  box-shadow: 0 0 12px rgba(0, 246, 255, 0.25);
}

.unit-card.queued {
  box-shadow: 0 0 12px rgba(0, 246, 255, 0.35);
}

.unit-card.busy {
  cursor: not-allowed;
  opacity: 0.55;
}

.unit-status {
  font-size: 0.75rem;
  color: #ebcb8b;
}

.drag-hint {
  text-align: center;
  color: #8892b0;
  font-size: 0.8rem;
  margin: 0 0 16px;
}

.drag-hint.mobile-only {
  display: none;
}

.troop-summary {
  display: block;
  margin-top: 4px;
  font-size: 0.75rem;
  color: #88c0d0;
  font-weight: 500;
}

.roster-row {
  align-items: flex-start;
  gap: 12px;
}

.roster-row .stat-label {
  flex: 1;
}

.drawer-backdrop {
  display: none;
}

.drawer-header,
.mobile-drawer-close {
  display: none;
}

@media (max-width: 768px) {
  .panel-troop-assign {
    display: none;
  }

  .drag-hint.desktop-only {
    display: none;
  }

  .drag-hint.mobile-only {
    display: block;
  }

  .drawer-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    z-index: 1190;
  }

  .side-info-panel {
    top: 0;
    right: 0;
    bottom: 0;
    width: 86vw;
    max-width: 380px;
    height: 100dvh;
    z-index: 1200;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.7);
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    pointer-events: none;
  }

  .side-info-panel.is-open {
    transform: translateX(0);
    pointer-events: auto;
  }

  .materialize-anim {
    animation: none;
    opacity: 1;
  }

  .glass-panel {
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .mobile-drawer-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    flex-shrink: 0;
    padding: 0.9rem 1rem;
    border-bottom: 1px solid rgba(0, 246, 255, 0.2);
    background: rgba(36, 40, 59, 0.6);
  }

  .mobile-drawer-header h2 {
    margin: 0;
    font-size: 1rem;
    color: #00f6ff;
    font-family: "Orbitron", sans-serif;
  }

  .panel-tabs {
    align-items: center;
    padding-right: 0.45rem;
  }

  .mobile-drawer-close,
  .drawer-close {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    padding: 0;
    margin: 0;
    line-height: 1;
    background: none;
    border: 1px solid rgba(0, 246, 255, 0.3);
    color: #a0a0b0;
    border-radius: 6px;
    cursor: pointer;
  }

  .drawer-close:hover {
    color: #00f6ff;
    border-color: #00f6ff;
  }
}
</style>
